import {
  Component,
  computed,
  EventEmitter,
  input,
  Output,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { DateTime } from "luxon";
import { environment } from "../../../../environments/environment";
import { Organizer } from "@services/types/organizer.type";

@Component({
  selector: "app-producers-header",
  templateUrl: "./header-producers-page.component.html",
  imports: [MatButtonModule, MatIconModule],
  styleUrls: ["./header-producers-page.component.scss"],
})
export class ProducersHeaderComponent {
  private readonly defaultCoverImage =
    "assets/images/default-cover-producers-page.jpg";
  private readonly defaultProfileImage = "assets/images/profile-image.png";

  organizer = input<Organizer | null>(null);
  numberOfEvents = input("");
  isFollowed = input(false);
  isOwnProfile = input(false);
  isLoggedIn = input(false);

  @Output() followProducerClick = new EventEmitter<void>();

  readonly coverImageUrl = computed(() =>
    this.resolveImageUrl(
      this.organizer()?.banner ?? null,
      this.defaultCoverImage,
    ),
  );
  readonly profileImageUrl = computed(() =>
    this.resolveImageUrl(
      this.organizer()?.thumbnail ?? null,
      this.defaultProfileImage,
    ),
  );
  readonly name = computed(() => {
    const organizer = this.organizer();
    if (!organizer) return "";
    if (organizer.company) return organizer.company_name ?? "";
    return [organizer.name, organizer.last_name].filter(Boolean).join(" ");
  });
  readonly producerSince = computed(() => {
    const createdAt = this.organizer()?.created_at;
    return createdAt ? this.formatProducerSince(createdAt) : "";
  });

  readonly socialLinks = computed(() => {
    const organizer = this.organizer();
    if (!organizer) return [];

    const links: { label: string; icon: string; url: string }[] = [];

    if (organizer.instagram_url) {
      links.push({
        label: "Instagram",
        icon: "pi pi-instagram",
        url: this.normalizeUrl(organizer.instagram_url),
      });
    }
    if (organizer.facebook_url) {
      links.push({
        label: "Facebook",
        icon: "pi pi-facebook",
        url: this.normalizeUrl(organizer.facebook_url),
      });
    }
    if (organizer.whatsapp_url) {
      links.push({
        label: "WhatsApp",
        icon: "pi pi-whatsapp",
        url: this.normalizeUrl(organizer.whatsapp_url),
      });
    }

    return links;
  });

  handleFollowProducerClick() {
    this.followProducerClick.emit();
  }

  private resolveImageUrl(value: string | null, fallback: string): string {
    if (!value) return fallback;
    if (/^https?:\/\//i.test(value)) return value;
    return `${environment.BLOB_URL}/${value}`;
  }

  private normalizeUrl(value: string): string {
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  }

  private formatProducerSince(isoDate: string): string {
    const dt = DateTime.fromISO(isoDate).setLocale("pt-BR");
    if (!dt.isValid) return "";
    const formatted = dt.toFormat("LLLL 'de' yyyy");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
}
